/**
 * Reusable Access Message Component for Enterprise Authentication
 *
 * Provides consistent UI for authentication and authorization messages
 * with proper accessibility and theming support.
 */
import React from 'react';
// import { logger } from "../../utils/logger";
import { Box, Typography, Button, Alert, AlertTitle, Divider, List, ListItem, ListItemText, Card, CardContent, Container, Paper, Stack, Chip, Fade, Slide, useTheme } from '@mui/material';
import { Lock as LockIcon, Security as SecurityIcon, Warning as WarningIcon, Info as InfoIcon, Login as LoginIcon, ArrowForward as ArrowForwardIcon, Shield as ShieldIcon, CheckCircle as CheckCircleIcon, TwoWheeler as TwoFactorIcon } from '@mui/icons-material';
import { logger } from "../../utils/logger";
export interface AccessMessageProps {
    /** Message title */
    title: string;
    /** Main message text */
    message: string;
    /** Additional details to display */
    details?: string[];
    /** Action button label */
    actionLabel: string;
    /** Action button click handler */
    onAction: () => void;
    /** Message type for styling */
    type?: 'auth_required' | 'access_denied' | 'error' | 'info' | 'mfa_required' | 'password_change_required';
    /** Whether to show the action button */
    showAction?: boolean;
    /** Additional action button (optional) */
    secondaryAction?: {
        label: string;
        onClick: () => void;
        variant?: 'text' | 'outlined' | 'contained';
    };
    /** Custom icon */
    icon?: React.ReactNode;
    /** Additional content to render */
    children?: React.ReactNode;
}
const AccessMessage: React.FC<AccessMessageProps> = ({ title, message, details = [], actionLabel, onAction, type = 'auth_required', showAction = true, secondaryAction, icon, children }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    // Get appropriate icon and styling based on type
    const getTypeConfig = () => {
        switch (type) {
            case 'auth_required':
                return {
                    icon: <LockIcon sx={{ fontSize: 64, color: 'primary.main' }}/>,
                    severity: 'info' as const,
                    color: 'primary.main',
                    bgColor: 'primary.50',
                    borderColor: 'primary.200'
                };
            case 'access_denied':
                return {
                    icon: <SecurityIcon sx={{ fontSize: 64, color: 'error.main' }}/>,
                    severity: 'warning' as const,
                    color: 'error.main',
                    bgColor: 'error.50',
                    borderColor: 'error.200'
                };
            case 'error':
                return {
                    icon: <WarningIcon sx={{ fontSize: 64, color: 'error.main' }}/>,
                    severity: 'error' as const,
                    color: 'error.main',
                    bgColor: 'error.50',
                    borderColor: 'error.200'
                };
            case 'info':
                return {
                    icon: <InfoIcon sx={{ fontSize: 64, color: 'info.main' }}/>,
                    severity: 'info' as const,
                    color: 'info.main',
                    bgColor: 'info.50',
                    borderColor: 'info.200'
                };
            case 'mfa_required':
                return {
                    icon: <TwoFactorIcon sx={{ fontSize: 64, color: theme.palette.warning.main }}/>,
                    severity: 'warning' as const,
                    color: theme.palette.warning.main,
                    bgColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)',
                    borderColor: theme.palette.warning.light
                };
            case 'password_change_required':
                return {
                    icon: <LockIcon sx={{ fontSize: 64, color: theme.palette.warning.main }}/>,
                    severity: 'warning' as const,
                    color: theme.palette.warning.main,
                    bgColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)',
                    borderColor: theme.palette.warning.light
                };
            default:
                return {
                    icon: <LockIcon sx={{ fontSize: 64, color: 'primary.main' }}/>,
                    severity: 'info' as const,
                    color: 'primary.main',
                    bgColor: 'primary.50',
                    borderColor: 'primary.200'
                };
        }
    };
    const typeConfig = getTypeConfig();
    return (<Container maxWidth="sm" sx={{ py: 8 }}>
      <Fade in timeout={800}>
        <Card elevation={0} sx={{
            borderRadius: 3,
            border: `1px solid`,
            borderColor: typeConfig.borderColor,
            background: `linear-gradient(135deg, ${typeConfig.bgColor} 0%, ${theme.palette.background.paper} 100%)`,
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${typeConfig.color} 0%, ${typeConfig.color}80 100%)`
            }
        }}>
          <CardContent sx={{ p: 6 }}>
            <Stack spacing={4} alignItems="center">
              {/* Icon with animated background */}
              <Slide direction="down" in timeout={1000}>
                <Box sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${typeConfig.bgColor} 0%, ${typeConfig.bgColor}80 100%)`,
            border: `2px solid ${typeConfig.borderColor}`,
            boxShadow: `0 8px 32px ${typeConfig.color}20`,
            '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${typeConfig.color}20, transparent)`,
                zIndex: -1
            }
        }}>
                  {icon || typeConfig.icon}
                </Box>
              </Slide>

              {/* Title with enhanced typography */}
              <Slide direction="up" in timeout={1200}>
                <Typography variant="h3" component="h1" textAlign="center" sx={{
            fontWeight: 700,
            color: typeConfig.color,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            mb: 1
        }}>
                  {title}
                </Typography>
              </Slide>

              {/* Subtitle with better spacing */}
              <Slide direction="up" in timeout={1400}>
                <Typography variant="h6" textAlign="center" sx={{
            color: 'text.secondary',
            fontWeight: 400,
            lineHeight: 1.6,
            maxWidth: '480px',
            mx: 'auto'
        }}>
                  {message}
                </Typography>
              </Slide>

              {/* Enhanced Details Section */}
              {details.length > 0 && (<Slide direction="up" in timeout={1600}>
                  <Alert severity={typeConfig.severity} sx={{
                width: '100%',
                borderRadius: 2,
                border: `1px solid ${typeConfig.borderColor}`,
                '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                }
            }}>
                    <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                      Additional Information
                    </AlertTitle>
                    <List dense sx={{ py: 0 }}>
                      {details.map((detail, index) => (<ListItem key={index} sx={{
                    py: 0.5,
                    px: 0,
                    '&:not(:last-child)': {
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }
                }}>
                          <ListItemText primary={detail} primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    fontWeight: 500
                }}/>
                        </ListItem>))}
                    </List>
                  </Alert>
                </Slide>)}

              {/* Custom Content */}
              {children && (<Slide direction="up" in timeout={1800}>
                  <Box sx={{ width: '100%' }}>
                    {children}
                  </Box>
                </Slide>)}

              {/* Enhanced Action Buttons */}
              {showAction && (<Slide direction="up" in timeout={2000}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{
                width: '100%',
                maxWidth: 400,
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                    <Button variant="contained" size="large" onClick={() => {
                logger.debug('🔘 AccessMessage button clicked, calling onAction');
                onAction();
            }} startIcon={type === 'auth_required' ? <LoginIcon /> :
                type === 'mfa_required' ? <TwoFactorIcon /> :
                    <ArrowForwardIcon />} sx={{
                minWidth: '180px',
                py: 1.8,
                px: 4,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${typeConfig.color} 0%, ${typeConfig.color}dd 100%)`,
                boxShadow: `0 4px 20px ${typeConfig.color}40`,
                '&:hover': {
                    background: `linear-gradient(135deg, ${typeConfig.color}dd 0%, ${typeConfig.color}bb 100%)`,
                    boxShadow: `0 6px 25px ${typeConfig.color}50`,
                    transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                      {actionLabel}
                    </Button>

                    {secondaryAction && (<Button variant={secondaryAction.variant || 'outlined'} size="large" onClick={secondaryAction.onClick} sx={{
                    minWidth: '180px',
                    py: 1.8,
                    px: 4,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: typeConfig.borderColor,
                    color: typeConfig.color,
                    '&:hover': {
                        borderColor: typeConfig.color,
                        backgroundColor: `${typeConfig.bgColor}80`,
                        transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                        {secondaryAction.label}
                      </Button>)}
                  </Stack>
                </Slide>)}

              {/* Security Badge */}
              <Slide direction="up" in timeout={2200}>
                <Chip icon={<ShieldIcon />} label="Secure Authentication" size="small" sx={{
            backgroundColor: `${typeConfig.bgColor}80`,
            color: typeConfig.color,
            border: `1px solid ${typeConfig.borderColor}`,
            fontWeight: 500
        }}/>
              </Slide>
            </Stack>
          </CardContent>
        </Card>
      </Fade>
    </Container>);
};
export default AccessMessage;
